// QuestionModal.js – Modal para agregar nuevas preguntas al survey
import { useState, useMemo, useCallback, memo } from 'react';
import {
  Box, TextField, Button, Typography, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, InputLabel, Select, MenuItem, FormControlLabel,
  Radio, Switch, IconButton, Tooltip, Alert,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon            from '@mui/icons-material/Close';
import VisibilityIcon       from '@mui/icons-material/Visibility';
import HelpOutlineIcon      from '@mui/icons-material/HelpOutline';

import {
  EMOJI_TYPES,
  getNextOptionId,
  QuestionImageUpload,
  INITIAL_QUESTION,
} from './Questions';

const QuestionModal = memo(({
  open,
  onClose,
  onAddQuestion,
  t,
  disableAdd = false,
  maxQuestions,
  questionCount,
  subscriptionActive,
}) => {
  const [text, setText]                   = useState('');
  const [type, setType]                   = useState('slider');
  const [emojiConfig, setEmojiConfig]     = useState(t.defaultEmojiConfig);
  const [options, setOptions]             = useState([]);
  const [newOptionText, setNewOptionText] = useState('');
  const [textError, setTextError]         = useState('');
  const [imageUrl, setImageUrl]           = useState('');

  const isEmojiType           = useMemo(() => EMOJI_TYPES.has(type), [type]);
  const filteredQuestionTypes = useMemo(
    () => t.questionTypes.filter((qt) => qt.value !== 'text'),
    [t.questionTypes],
  );

  const validateQuestionText = useCallback(() => {
    const trimmed = text.trim();
    if (trimmed.length < 5) { setTextError(t.questionTextErrorShort); return false; }
    if (text.length > 200)  { setTextError(t.questionTextErrorLong);  return false; }
    setTextError('');
    return true;
  }, [text, t]);

  const handleAddOption = useCallback(() => {
    const trimmed = newOptionText.trim();
    if (!trimmed) return;
    setOptions((prev) => [...prev, { id: getNextOptionId(prev), text: trimmed }]);
    setNewOptionText('');
  }, [newOptionText]);

  const handleRemoveOption = useCallback(
    (optionId) => setOptions((prev) => prev.filter((o) => o.id !== optionId)),
    [],
  );

  const handleOptionTextChange = useCallback(
    (optionId, newText) =>
      setOptions((prev) => prev.map((o) => (o.id === optionId ? { ...o, text: newText } : o))),
    [],
  );

  const handleKeyDown = useCallback(
    (e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddOption(); } },
    [handleAddOption],
  );

  const handleEmojiConfigChange = useCallback(
    (field, value) => setEmojiConfig((prev) => ({ ...prev, [field]: value })),
    [],
  );

  const handleTypeChange = useCallback(
    (e) => {
      const newType = e.target.value;
      setType(newType);
      setEmojiConfig(
        newType === 'emojislike' ? t.defaultEmojiLikeConfig : t.defaultEmojiConfig,
      );
    },
    [t],
  );

  const resetModalState = useCallback(() => {
    setText('');
    setType('slider');
    setImageUrl('');
    setEmojiConfig(t.defaultEmojiConfig);
    setOptions([]);
    setNewOptionText('');
    setTextError('');
  }, [t]);

  const handleAdd = useCallback(() => {
    if (disableAdd || !validateQuestionText()) return;
    onAddQuestion({
      text: text.trim(),
      type,
      url: imageUrl,
      ...(type === 'slider' && { sliderConfig: { min: 1, max: 5 } }),
      ...(isEmojiType       && { config: emojiConfig }),
      ...(type === 'radio'  && { options }),
    });
    resetModalState();
    onClose();
  }, [
    disableAdd, validateQuestionText, text, type, imageUrl,
    emojiConfig, options, isEmojiType,
    onAddQuestion, resetModalState, onClose,
  ]);

  const handleModalClose = useCallback(() => {
    resetModalState();
    onClose();
  }, [resetModalState, onClose]);

  return (
    <Dialog open={open} onClose={handleModalClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t.addQuestionModalTitle}</DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1, mb: 1 }}>
          <QuestionImageUpload
            qId="modal-new"
            imageUrl={imageUrl}
            onImageChange={setImageUrl}
            subscriptionActive={subscriptionActive}
            t={t}
          />
        </Box>

        <TextField
          autoFocus
          margin="dense"
          label={t.questionTextLabel}
          fullWidth
          value={text}
          onChange={(e) => setText(e.target.value)}
          error={!!textError}
          helperText={textError}
          multiline
          minRows={2}
          inputProps={{ maxLength: 200 }}
        />

        <FormControl fullWidth margin="dense">
          <InputLabel>{t.questionTypeLabel}</InputLabel>
          <Select value={type} label={t.questionTypeLabel} onChange={handleTypeChange}>
            {filteredQuestionTypes.map((tType) => (
              <MenuItem key={tType.value} value={tType.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tType.icon}
                  <Typography>{tType.label}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ── Slider config ── */}
        {type === 'slider' && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              {t.sliderConfigTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">{t.sliderRange5}</Typography>
          </Box>
        )}

        {/* ── Emoji config ── */}
        {isEmojiType && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              {t.emojiConfigTitle}
            </Typography>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={emojiConfig.show}
                    onChange={(e) => handleEmojiConfigChange('show', e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <VisibilityIcon fontSize="small" />
                    <Typography variant="body2">{t.showTextLabel}</Typography>
                    <Tooltip title={t.showTextTooltip}>
                      <HelpOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </Tooltip>
                  </Box>
                }
                sx={{ m: 0 }}
              />
            </Box>
            {emojiConfig.show && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label={t.leftTextLabel}
                  value={emojiConfig.left}
                  onChange={(e) => handleEmojiConfigChange('left', e.target.value)}
                  placeholder={t.leftTextPlaceholder}
                  fullWidth
                  variant="outlined"
                  helperText={t.leftTextHelper}
                  inputProps={{ maxLength: 40 }}
                />
                <Typography variant="body2" color="text.secondary">↔</Typography>
                <TextField
                  size="small"
                  label={t.rightTextLabel}
                  value={emojiConfig.right}
                  onChange={(e) => handleEmojiConfigChange('right', e.target.value)}
                  placeholder={t.rightTextPlaceholder}
                  fullWidth
                  variant="outlined"
                  helperText={t.rightTextHelper}
                  inputProps={{ maxLength: 40 }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* ── Radio options ── */}
        {type === 'radio' && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              {t.radioOptionsTitle}
            </Typography>
            {options.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                {t.radioOptionsEmpty}
              </Typography>
            )}
            {options.map((option, index) => (
              <Box key={option.id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Radio disabled size="small" />
                <TextField
                  size="small"
                  value={option.text}
                  onChange={(e) => handleOptionTextChange(option.id, e.target.value)}
                  placeholder={t.optionPlaceholder.replace('{index}', index + 1)}
                  fullWidth
                  variant="outlined"
                  sx={{ ml: 1 }}
                  inputProps={{ maxLength: 100 }}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveOption(option.id)}
                  sx={{ ml: 1 }}
                  aria-label={t.removeOptionAriaLabel}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <TextField
                size="small"
                value={newOptionText}
                onChange={(e) => setNewOptionText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t.newOptionPlaceholder}
                fullWidth
                variant="outlined"
                inputProps={{ maxLength: 100 }}
              />
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddOption}
                size="small"
                variant="outlined"
                sx={{ ml: 1, whiteSpace: 'nowrap' }}
              >
                {t.addButton}
              </Button>
            </Box>
          </Box>
        )}

        {disableAdd && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t.maxQuestionsReachedMsg
              .replace('{max}', maxQuestions)
              .replace('{count}', questionCount)}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleModalClose} color="secondary">{t.cancelButton}</Button>
        <Button onClick={handleAdd} color="primary" variant="contained" disabled={disableAdd}>
          {t.addButtonModal}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

QuestionModal.displayName = 'QuestionModal';

export default QuestionModal;